<script setup lang="ts">
import { useLoginSpeckle } from "@/composables/useLoginSpeckle";
import {
  getEquationExcelClientMetadata,
  getTransferProfileKey,
  getVisibleExcelProfiles,
  hasSpeckleTransferProfile,
  isSpeckleModelForApp,
  type SpeckleTransferProfileOption,
} from "@/services/equation-excel-client-metadata/equationExcelClientMetadata";
import { useApiStore } from "@/stores/api/useApiStore";
import type { SpeckleModel } from "@haskoning/js-speckle";
import { HaskoningButton, HaskoningText } from "@haskoning/vue-component-library";
import { SpeckleModelSelect, SpeckleVersionSelectField, useSpeckleStore } from "@haskoning/vue-speckle";
import { computed, onMounted, ref, shallowRef, toRaw, watch } from "vue";

import { useReceiveSpeckleProfile } from "../composables/useReceiveSpeckleProfile";
import { useSpeckleContextSelection } from "../composables/useSpeckleContextSelection";
import ReceiveSpeckleOverwriteDialog from "./ReceiveSpeckleOverwriteDialog.vue";
import SpeckleProjectSelect from "./SpeckleProjectSelect.vue";

const speckleStore = useSpeckleStore({ autoSelectData: false });
const apiStore = useApiStore();

const { hasSelectedProject, hasSelectedModel, isContextComplete } = useSpeckleContextSelection();
const {
  hasSpeckleSessionExpired,
  isLoggingIn,
  isSpeckleReady,
  loginError,
  loginSpeckle,
  logoutSpeckle,
  restoreSpeckleSession,
} = useLoginSpeckle();
const {
  receivingProfileKey,
  receiveIntoWorkbook,
  overwriteDialogOpen,
  pendingReceiveWrite,
  isWritingOverwrite,
  cancelReceiveOverwrite,
  confirmReceiveOverwrite,
} = useReceiveSpeckleProfile();

const speckleTransferProfiles = computed<SpeckleTransferProfileOption[]>(() => {
  const result: SpeckleTransferProfileOption[] = [];

  for (const [url, entry] of apiStore.apiEntries) {
    if (entry.state !== "loaded") {
      continue;
    }

    const schema = apiStore.getCachedSchema(url);
    const excelClient = schema ? getEquationExcelClientMetadata(schema) : undefined;

    if (!excelClient) {
      continue;
    }

    for (const visibleProfile of getVisibleExcelProfiles(excelClient)) {
      if (hasSpeckleTransferProfile(visibleProfile.profile)) {
        result.push({ ...visibleProfile, apiUrl: url });
      }
    }
  }

  return result;
});

const hasSpeckleTransfer = computed<boolean>(() => speckleTransferProfiles.value.length > 0);

const projectModels = shallowRef<SpeckleModel[]>([]);

let lastVisibleWrite: object | null = null;

watch(
  () => speckleStore.currentSelection.project?.id,
  () => {
    projectModels.value = [];
  }
);

watch(
  () => speckleStore.allModels,
  (models) => {
    if (toRaw(models) === lastVisibleWrite) {
      return;
    }

    projectModels.value = models;
  },
  { immediate: true }
);

function hasSameModels(current: SpeckleModel[], next: SpeckleModel[]): boolean {
  return current.length === next.length && current.every((model, index) => model.id === next[index].id);
}

function applyModelFilter(): void {
  const filtered = projectModels.value.filter((model) =>
    speckleTransferProfiles.value.some((profile) => isSpeckleModelForApp(profile.app, model))
  );

  if (hasSameModels(speckleStore.allModels, filtered)) {
    return;
  }

  lastVisibleWrite = filtered;
  speckleStore.allModels = filtered;
}

watch([projectModels, speckleTransferProfiles], applyModelFilter, { immediate: true });

const chosenProfileKey = ref<string | null>(null);

const visibleTransferProfiles = computed<SpeckleTransferProfileOption[]>(() => {
  const selectedModel = speckleStore.currentSelection.model;

  if (!selectedModel) {
    return [];
  }

  return speckleTransferProfiles.value.filter((profile) => isSpeckleModelForApp(profile.app, selectedModel));
});

const selectedTransferProfile = computed<SpeckleTransferProfileOption | null>(() => {
  const profiles = visibleTransferProfiles.value;

  return profiles.find((profile) => getTransferProfileKey(profile) === chosenProfileKey.value) ?? profiles[0] ?? null;
});

const selectedProfileKey = computed<string | null>({
  get: () => (selectedTransferProfile.value ? getTransferProfileKey(selectedTransferProfile.value) : null),
  set: (value) => {
    chosenProfileKey.value = value;
  },
});

const profileOptions = computed(() =>
  visibleTransferProfiles.value.map((profile) => ({
    key: getTransferProfileKey(profile),
    label: `${profile.app.app_name} / ${profile.profile.profile_name}`,
  }))
);

const isProfileDisabled = computed<boolean>(() => visibleTransferProfiles.value.length === 0);

const isReceiving = computed<boolean>(
  () =>
    selectedTransferProfile.value !== null &&
    receivingProfileKey.value === getTransferProfileKey(selectedTransferProfile.value)
);

function handleReceive(): void {
  if (selectedTransferProfile.value) {
    void receiveIntoWorkbook(selectedTransferProfile.value);
  }
}

onMounted(async () => {
  await restoreSpeckleSession();
});
</script>

<template>
  <section v-if="hasSpeckleTransfer" class="d-flex flex-column ga-3">
    <HaskoningText as="span" variant="title-small" font-weight="medium" color="primary">
      Speckle transfer
    </HaskoningText>

    <SpeckleProjectSelect :is-speckle-ready="isSpeckleReady" :is-logging-in="isLoggingIn" @login="loginSpeckle" />

    <div v-if="isSpeckleReady" class="d-flex justify-end">
      <HaskoningButton variant="text" color="secondary" size="small" @click="logoutSpeckle">
        Sign out of Speckle
      </HaskoningButton>
    </div>

    <v-alert v-if="hasSpeckleSessionExpired" type="warning" variant="tonal" density="compact">
      <div class="d-flex flex-column ga-2 align-start">
        <HaskoningText as="span" variant="body-small">
          Your Speckle session has expired. Sign in again to continue.
        </HaskoningText>

        <HaskoningButton color="primary" size="small" :loading="isLoggingIn" @click="loginSpeckle">
          Sign in to Speckle
        </HaskoningButton>
      </div>
    </v-alert>

    <HaskoningText v-if="loginError" as="p" variant="body-small" color="error">
      {{ loginError }}
    </HaskoningText>

    <SpeckleModelSelect
      label="Model"
      variant="filled"
      bg-color="surfaceContainerHighest"
      :disabled="!isSpeckleReady || !hasSelectedProject"
    />

    <SpeckleVersionSelectField
      label="Version"
      variant="filled"
      bg-color="surfaceContainerHighest"
      :disabled="!isSpeckleReady || !hasSelectedModel"
    />

    <v-select
      v-model="selectedProfileKey"
      :items="profileOptions"
      item-title="label"
      item-value="key"
      label="Profile"
      variant="filled"
      density="comfortable"
      bg-color="surfaceContainerHighest"
      :disabled="!isSpeckleReady || isProfileDisabled"
      hide-details
    />

    <div class="d-flex">
      <HaskoningButton
        color="primary"
        variant="outlined"
        shape="square"
        append-icon="mdi-tray-arrow-down"
        :loading="isReceiving"
        :disabled="!isContextComplete || !isSpeckleReady || !selectedTransferProfile"
        @click="handleReceive"
      >
        Receive
      </HaskoningButton>
    </div>

    <ReceiveSpeckleOverwriteDialog
      v-model="overwriteDialogOpen"
      :pending-write="pendingReceiveWrite"
      :loading="isWritingOverwrite"
      @cancel="cancelReceiveOverwrite"
      @confirm="confirmReceiveOverwrite"
    />
  </section>
</template>

<style scoped>
:deep(.v-field--variant-filled.v-field--focused .v-field__overlay) {
  opacity: 0;
}
</style>
